import React, { useState, useEffect } from "react";
import { styled } from "@mui/system";
import {
  Box,
  IconButton,
  Typography,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Fullscreen, FullscreenExit } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import { ChatHistoryToggle } from "./ChatHistoryPanel";
import chatbot from "../../../assets/images/layout/Seal_of_Maharashtra.png";
import { useLocation } from "react-router-dom";
import apiClient from "../../../services/apiClient";

const Header = styled(Box)(({ theme, fullScreen }) => ({
  backgroundColor: "#f1f3f4",
  height: fullScreen ? "90px" : "75px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: fullScreen ? "0 32px" : "0 24px",
  borderBottom: "1px solid rgba(24, 116, 210, 0.12)",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
  flexShrink: 0,
  minHeight: 0,
  overflow: "hidden",
  position: "relative",
  zIndex: 10,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "@media (max-width: 960px)": {
    height: fullScreen ? "85px" : "70px",
    padding: fullScreen ? "0 24px" : "0 20px",
  },
  "@media (max-width: 768px)": {
    height: fullScreen ? "80px" : "70px",
    padding: fullScreen ? "0 20px" : "0 16px",
    flexWrap: "nowrap",
  },
  "@media (max-width: 600px)": {
    height: fullScreen ? "75px" : "65px",
    padding: fullScreen ? "0 16px" : "0 12px",
    flexWrap: "nowrap",
  },
  "@media (max-width: 480px)": {
    height: fullScreen ? "70px" : "65px",
    minHeight: fullScreen ? "70px" : "65px",
    padding: fullScreen ? "8px 12px" : "8px 10px",
    flexWrap: "nowrap",
  },
}));

const TitleContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 16,
  flexWrap: "nowrap",
  flex: "0 0 auto",
  minWidth: 0,
  overflow: "hidden",
  "@media (max-width: 768px)": {
    gap: 12,
    flex: "0 0 auto",
    minWidth: 0,
  },
  "@media (max-width: 480px)": {
    gap: 8,
    flex: "0 0 auto",
    minWidth: 0,
  },
});

const Title = styled(Typography)(({ fullScreen }) => ({
  color: "#002855",
  fontWeight: 600,
  fontSize: fullScreen ? "1.4rem" : "1.1rem",
  textAlign: "left",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  letterSpacing: "-0.02em",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "@media (max-width: 960px)": {
    fontSize: fullScreen ? "1.2rem" : "1rem",
  },
  "@media (max-width: 768px)": {
    fontSize: fullScreen ? "1.1rem" : "0.95rem",
  },
  "@media (max-width: 600px)": {
    fontSize: fullScreen ? "1rem" : "0.9rem",
  },
  "@media (max-width: 480px)": {
    fontSize: fullScreen ? "0.95rem" : "0.85rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const RightSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flex: "0 0 auto",
  minWidth: 0,
  flexShrink: 0,
  "@media (max-width: 768px)": {
    gap: "6px",
  },
  "@media (max-width: 480px)": {
    gap: "4px",
    flexShrink: 0,
  },
});

function ChatHeader({
  handleOpenClick,
  fullScreen,
  selectedDepartments = ["all"],
  onDepartmentChange,
  chatMode = "normal",
  useMemory = false,
  onUseMemoryChange,
  showInlineContext = true,
  onToggleInlineContext,
}) {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  /** Show label in fullscreen; in embedded mode only on long-title layouts. */
  const showUseMemoryLabel =
    fullScreen ||
    (location.pathname !== "/" && location.pathname !== "/promotion");
  const [gadTypes, setGadTypes] = useState([]);
  const [hasSetDefault, setHasSetDefault] = useState(false);
  const ALL_VALUE = "all";
  const SERVICE_BOOK = "Service_Book";

  // Fetch GAD types from API
  useEffect(() => {
    const fetchGadTypes = async () => {
      try {
        const response = await apiClient.get("/section_page/gad_type");
        setGadTypes(response.data || []);
      } catch (error) {
        console.error("Error fetching GAD types: ", error);
        setGadTypes([]); // Set empty array on error
      }
    };
    fetchGadTypes();
  }, []);

  // Reset default flag whenever route changes so we can re-evaluate defaults per page
  useEffect(() => {
    setHasSetDefault(false);
  }, [location.pathname]);

  // Set route-aware default gad_type after GAD types are loaded
  useEffect(() => {
    if (
      !gadTypes.length ||
      hasSetDefault ||
      !Array.isArray(selectedDepartments)
    ) {
      return;
    }

    const isPromotionPage = location.pathname === "/promotion";

    if (isPromotionPage) {
      setHasSetDefault(true);
      onDepartmentChange?.(["GAD_promotion"]);
      return;
    }

    const hasOnlyAll =
      selectedDepartments.length === 0 ||
      (selectedDepartments.length === 1 &&
        selectedDepartments[0] === ALL_VALUE);

    if (hasOnlyAll) {
      setHasSetDefault(true);
      onDepartmentChange?.([gadTypes[0]]);
    }
  }, [
    gadTypes,
    selectedDepartments,
    onDepartmentChange,
    hasSetDefault,
    location.pathname,
  ]);

  const selectedGadType = Array.isArray(selectedDepartments)
    ? selectedDepartments[0]
    : selectedDepartments;

  const handleDepartmentChange = (event) => {
    const value = event?.target?.value;
    const nextValue = Array.isArray(value) ? value[0] : value;
    if (nextValue === undefined || nextValue === null) return;

    const next =
      String(nextValue) === ALL_VALUE ? (gadTypes[0] ?? "") : String(nextValue);
    if (!next) return;

    setHasSetDefault(true);
    onDepartmentChange?.([next]);
  };

  return (
    <Header fullScreen={fullScreen}>
      {/* Left section with avatar and title */}
      <TitleContainer>
        <Avatar
          alt="Bot Avatar"
          src={chatbot}
          variant="square"
          sx={{
            width: {
              xs: fullScreen ? "40px" : "32px",
              sm: fullScreen ? "44px" : "34px",
              md: fullScreen ? "48px" : "36px",
            },
            height: {
              xs: fullScreen ? "40px" : "32px",
              sm: fullScreen ? "44px" : "34px",
              md: fullScreen ? "48px" : "36px",
            },
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(24, 116, 210, 0.15)",
            border: "2px solid rgba(24, 116, 210, 0.1)",
            flexShrink: 0,
            display: { xs: "none", sm: "flex" },
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            flex: "0 0 auto",
            minWidth: 0,
          }}
        >
          <Title variant="body1" fullScreen={fullScreen}>
            {fullScreen ||
            location.pathname === "/" ||
            location.pathname === "/promotion"
              ? "GAD"
              : "General Administration Department Chatbot"}
          </Title>

          {fullScreen && (
            <Typography
              variant="caption"
              sx={{
                color: "#666666",
                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                marginTop: "-2px",
                fontWeight: 400,
                letterSpacing: "0.01em",
                display: { xs: "none", sm: "block" },
              }}
            >
              GAD Chatbot - AI Assistant
            </Typography>
          )}
        </Box>
      </TitleContainer>

      {/* Center section with dropdown */}
      {(location.pathname === "/" ||
        location.pathname === "/promotion" ||
        fullScreen) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: { xs: "1 1 auto", sm: 1 },
            minWidth: 0,
            margin: { xs: "0 8px", sm: "0 16px", md: "0 24px" },
          }}
        >
          <FormControl
            size="small"
            sx={{
              minWidth: {
                xs: fullScreen ? 120 : 100,
                sm: fullScreen ? 160 : 140,
                md: fullScreen ? 220 : 160,
              },
              maxWidth: {
                xs: fullScreen ? 180 : 140,
                sm: fullScreen ? 220 : 180,
                md: fullScreen ? 280 : 200,
              },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Select
              value={
                selectedGadType && selectedGadType !== ALL_VALUE
                  ? selectedGadType
                  : (gadTypes[0] ?? "")
              }
              displayEmpty
              onChange={handleDepartmentChange}
              renderValue={(selected) => {
                const value = Array.isArray(selected) ? selected[0] : selected;
                if (!value) return "Select";
                if (value === SERVICE_BOOK) return "Service Book";

                const displayText = String(value);
                if (isMobile && displayText.length > 15) {
                  return `${displayText.substring(0, 12)}...`;
                }
                return displayText;
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: { xs: "8px", sm: "12px" },
                    boxShadow:
                      "0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)",
                    border: "1px solid rgba(24, 116, 210, 0.08)",
                    marginTop: { xs: "4px", sm: "8px" },
                    maxHeight: { xs: "250px", sm: "300px", md: "400px" },
                    maxWidth: { xs: "calc(100vw - 32px)", sm: "none" },
                    width: { xs: "auto", sm: "auto" },
                    overflowY: "auto",
                    "& .MuiList-root": {
                      padding: { xs: "4px", sm: "8px" },
                      maxHeight: "inherit",
                    },
                  },
                },
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: isMobile ? "center" : "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: isMobile ? "center" : "left",
                },
                getContentAnchorEl: null,
                disableScrollLock: true,
              }}
              sx={{
                borderRadius: "12px",
                backgroundColor: "#ffffff",
                height: {
                  xs: fullScreen ? "40px" : "36px",
                  sm: fullScreen ? "42px" : "38px",
                  md: fullScreen ? "44px" : "38px",
                },
                fontSize: {
                  xs: fullScreen ? "0.8rem" : "0.75rem",
                  sm: fullScreen ? "0.85rem" : "0.8rem",
                  md: fullScreen ? "0.9rem" : "0.85rem",
                },
                fontWeight: 500,
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid rgba(24, 116, 210, 0.16)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid rgba(24, 116, 210, 0.28)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "2px solid rgba(24, 116, 210, 1)",
                },
                "& .MuiSelect-select": {
                  padding: {
                    xs: fullScreen ? "6px 12px" : "6px 10px",
                    sm: fullScreen ? "7px 14px" : "7px 12px",
                    md: fullScreen ? "8px 16px" : "8px 14px",
                  },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            >
              {/* All intentionally removed from UI */}
              {gadTypes.map((type, index) => {
                const checked = selectedGadType === type;
                return (
                  <MenuItem
                    key={index}
                    value={type}
                    sx={{
                      borderRadius: { xs: "6px", sm: "8px" },
                      margin: { xs: "1px 0", sm: "2px 0" },
                      padding: { xs: "6px 10px", sm: "8px 12px" },
                      minHeight: { xs: "36px", sm: "40px" },
                      "&:hover": {
                        backgroundColor: "rgba(24, 116, 210, 0.08)",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "rgba(24, 116, 210, 0.12)",
                        "&:hover": {
                          backgroundColor: "rgba(24, 116, 210, 0.16)",
                        },
                      },
                    }}
                  >
                    <ListItemText
                      primary={type}
                      sx={{
                        "& .MuiListItemText-primary": {
                          fontSize: {
                            xs: "0.8rem",
                            sm: "0.85rem",
                            md: "0.9rem",
                          },
                          fontWeight: 500,
                          color: checked ? "rgba(24, 116, 210, 1)" : "#333333",
                          lineHeight: 1.4,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        },
                      }}
                    />
                  </MenuItem>
                );
              })}
              {/* Service Book - static exclusive option */}
              <MenuItem
                value={SERVICE_BOOK}
                sx={{
                  borderRadius: { xs: "6px", sm: "8px" },
                  margin: { xs: "1px 0", sm: "2px 0" },
                  padding: { xs: "6px 10px", sm: "8px 12px" },
                  minHeight: { xs: "36px", sm: "40px" },
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(24, 116, 210, 0.08)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(24, 116, 210, 0.12)",
                    "&:hover": {
                      backgroundColor: "rgba(24, 116, 210, 0.16)",
                    },
                  },
                }}
              >
                <ListItemText
                  primary="Service Book"
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: {
                        xs: "0.8rem",
                        sm: "0.85rem",
                        md: "0.9rem",
                      },
                      fontWeight: 500,
                      color:
                        selectedGadType === SERVICE_BOOK
                          ? "rgba(24, 116, 210, 1)"
                          : "#333333",
                      lineHeight: 1.4,
                    },
                  }}
                />
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Right-aligned Icons — tighter gap when not fullscreen (embedded widget) */}
      <RightSection
        sx={{
          gap: fullScreen ? undefined : { xs: "2px", sm: "3px", md: "4px" },
        }}
      >
        {selectedGadType !== SERVICE_BOOK && (
          <Tooltip title="Use History" placement="bottom">
            <span>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(useMemory)}
                    onChange={(event) =>
                      onUseMemoryChange?.(Boolean(event.target.checked))
                    }
                    inputProps={{
                      "aria-label": "Use History",
                    }}
                    sx={{
                      color: "rgba(24, 116, 210, 0.6)",
                      "&.Mui-checked": {
                        color: "rgba(24, 116, 210, 1)",
                      },
                      padding: "4px",
                    }}
                  />
                }
                label={showUseMemoryLabel ? "Use History" : ""}
                sx={{
                  margin: 0,
                  marginRight: fullScreen
                    ? { xs: "2px", md: "8px" }
                    : { xs: "0px", sm: "2px", md: "4px" },
                  "& .MuiFormControlLabel-label": {
                    display: showUseMemoryLabel ? "inline" : "none",
                    fontSize: { md: "0.72rem", lg: "0.75rem" },
                    color: "#555555",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  },
                }}
              />
            </span>
          </Tooltip>
        )}
        <ChatHistoryToggle fullScreen={fullScreen} />
        <Tooltip
          title={
            showInlineContext ? "Hide inline context" : "Show inline context"
          }
        >
          <IconButton
            onClick={onToggleInlineContext}
            aria-label="Toggle inline context icons"
            sx={{
              mr: fullScreen ? 1 : 0,
              backgroundColor: showInlineContext
                ? "rgba(24, 116, 210, 0.08)"
                : "transparent",
              border: showInlineContext
                ? "1px solid rgba(24, 116, 210, 0.2)"
                : "1px solid rgba(0, 0, 0, 0.08)",
              borderRadius: "10px",
              padding: {
                xs: fullScreen ? "8px" : "6px",
                sm: fullScreen ? "9px" : "7px",
                md: fullScreen ? "10px" : "8px",
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor: showInlineContext
                  ? "rgba(24, 116, 210, 0.12)"
                  : "rgba(0, 0, 0, 0.04)",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
              },
            }}
          >
            <InfoIcon
              sx={{
                color: showInlineContext ? "rgba(24, 116, 210, 1)" : "#666666",
                fontSize: {
                  xs: fullScreen ? "18px" : "16px",
                  sm: fullScreen ? "19px" : "17px",
                  md: fullScreen ? "20px" : "18px",
                },
              }}
            />
          </IconButton>
        </Tooltip>
        {fullScreen && (
          <Typography
            variant="caption"
            sx={{
              color: "#666",
              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
              marginRight: { xs: "4px", sm: "8px", md: "10px" },
              display: { xs: "none", md: "block" },
            }}
          >
            Press ESC to exit fullscreen
          </Typography>
        )}

        <IconButton
          onClick={handleOpenClick}
          aria-label={fullScreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          sx={{
            backgroundColor: fullScreen
              ? "rgba(24, 116, 210, 0.08)"
              : "transparent",
            border: fullScreen
              ? "1px solid rgba(24, 116, 210, 0.2)"
              : "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "10px",
            padding: {
              xs: fullScreen ? "8px" : "6px",
              sm: fullScreen ? "9px" : "7px",
              md: fullScreen ? "10px" : "8px",
            },
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor: fullScreen
                ? "rgba(24, 116, 210, 0.12)"
                : "rgba(0, 0, 0, 0.04)",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
            },
          }}
        >
          {fullScreen ? (
            <FullscreenExit
              sx={{
                color: "rgba(24, 116, 210, 1)",
                fontSize: {
                  xs: fullScreen ? "18px" : "16px",
                  sm: fullScreen ? "19px" : "17px",
                  md: fullScreen ? "20px" : "18px",
                },
              }}
            />
          ) : (
            <Fullscreen
              sx={{
                color: "#666666",
                fontSize: {
                  xs: fullScreen ? "18px" : "16px",
                  sm: fullScreen ? "19px" : "17px",
                  md: fullScreen ? "20px" : "18px",
                },
              }}
            />
          )}
        </IconButton>

        {/* <IconButton onClick={downloadPdf}>
          <Download sx={{ color: "#000000" }} />
        </IconButton>
        <IconButton onClick={sharePdf}>
          <Share sx={{ color: "#000000" }} />
        </IconButton> */}
      </RightSection>
    </Header>
  );
}

export default ChatHeader;
